import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Mail,
  Plus,
  Save,
  Search,
  Send,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService, employeeService } from "../../../services/api";
import { getAuthJson } from "../../../utils/authStorage";
import {
  LAYOUT_META,
  MERGE_TAGS,
  PAYSLIP_STYLE_TAGS,
  SECTION_BLOCKS,
  normalizeSectionOrder,
} from "../../../constants/payslipTemplateCatalog";
import PayrollActionChooserModal from "./PayrollActionChooserModal";
import PayrollReimbursementPayslipsPanel from "./PayrollReimbursementPayslipsPanel";
import PolicyApplicabilityStepSection from "../policy-wizard/PolicyApplicabilityStepSection";
import { chunkIds, filterEmployeesByApplicability, formatInr } from "./runPayrollUtils";
import { ProgressRing } from "./ProcessPayrollPanel";
import useConfirmDialog from "../../../hooks/useConfirmDialog";
import { usePermissions } from "../../../rbac/PermissionsContext";

const PUBLISH_CHUNK_SIZE = 5;

function monthYearLabel(payMonth) {
  if (!payMonth) return "";
  const [y, m] = payMonth.split("-").map(Number);
  const names = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${names[(m || 1) - 1]}-${y}`;
}

function tagEnabled(style, key) {
  return style?.[key] !== false;
}

/** Live A4-ish preview reflecting template tags. */
function PayslipMockPreview({ template, darkMode, payMonth, companyLogo, companyName }) {
  const s = template?.styleJson || {};
  const paper = darkMode ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] border-[#3C3C3C]" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]";
  const line = darkMode ? "border-[#333]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const label = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const monthLabel = monthYearLabel(payMonth) || payMonth || "April-2026";
  const orgName = companyName || "Moon Knight Solutions";

  const earn = [
    ["Basic", "62,500.00"],
    ["HRA", "25,000.00"],
    ["Special Allowance", "33,550.00"],
  ];
  const ded = [
    ["PF (Employee)", "1,800.00"],
    ["Professional Tax (PT)", "200.00"],
    ...(tagEnabled(s, "showTaxDetails") ? [["Income Tax (TDS)", "11,294.00"]] : []),
  ];

  return (
    <div className={`mx-auto w-full max-w-[480px] rounded-lg border shadow-sm ${paper}`}>
      <div className={`border-b px-4 py-3 ${line}`}>
        <div className="flex items-start gap-3">
          {tagEnabled(s, "showLogo") && (
            companyLogo ? (
              <img
                src={companyLogo}
                alt="Company logo"
                className="h-10 w-14 shrink-0 object-contain"
              />
            ) : (
              <div className="h-9 w-14 shrink-0 rounded bg-[hsl(var(--ds-primary))]/90 text-[9px] font-bold leading-9 text-center text-white">
                LOGO
              </div>
            )
          )}
          <div className="min-w-0 flex-1">
            {tagEnabled(s, "showCompanyName") && (
              <p className="text-sm font-bold">{orgName}</p>
            )}
            {tagEnabled(s, "showCompanyAddress") && (
              <p className={`text-[10px] ${muted}`}>
                12 Business Park, MG Road, Bengaluru, Karnataka 560001
              </p>
            )}
            {tagEnabled(s, "showCompanyStatutory") && (
              <p className={`mt-0.5 text-[9px] ${muted}`}>
                PAN: AABCM1234A | TAN: BLRM12345B | PF: KN/BGE/12345
              </p>
            )}
          </div>
        </div>
        {tagEnabled(s, "showPayPeriod") && (
          <div
            className={`mt-2 rounded px-2 py-1 text-center text-[11px] font-semibold ${
              darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"
            }`}
          >
            Payslip for {monthLabel}
          </div>
        )}
      </div>

      {(tagEnabled(s, "showEmployeeBasic") ||
        tagEnabled(s, "showEmployeeMasters") ||
        tagEnabled(s, "showEmployeeStatutory") ||
        tagEnabled(s, "showBankDetails")) && (
        <div className={`grid grid-cols-2 gap-x-3 gap-y-1 border-b px-4 py-2 text-[10px] ${line}`}>
          {tagEnabled(s, "showEmployeeBasic") && (
            <>
              <div>
                <span className={label}>Name: </span>
                <span className="font-medium">Kavin</span>
              </div>
              <div>
                <span className={label}>Code: </span>
                <span className="font-medium">MK0001</span>
              </div>
              <div>
                <span className={label}>DOJ: </span>
                <span>01/04/2024</span>
              </div>
            </>
          )}
          {tagEnabled(s, "showEmployeeMasters") && (
            <>
              <div>
                <span className={label}>Designation: </span>
                <span>Software Engineer</span>
              </div>
              <div>
                <span className={label}>Department: </span>
                <span>Engineering</span>
              </div>
            </>
          )}
          {tagEnabled(s, "showEmployeeStatutory") && (
            <>
              <div>
                <span className={label}>PAN: </span>
                <span>ABCDE1234F</span>
              </div>
              <div>
                <span className={label}>UAN: </span>
                <span>100234567890</span>
              </div>
            </>
          )}
          {tagEnabled(s, "showBankDetails") && (
            <div className="col-span-2">
              <span className={label}>Bank: </span>
              <span>HDFC ****4521 / HDFC0001234</span>
            </div>
          )}
        </div>
      )}

      {(tagEnabled(s, "showStructure") ||
        tagEnabled(s, "showAnnualCtc") ||
        tagEnabled(s, "showMonthlyCtc")) && (
        <div className={`border-b px-4 py-1.5 text-[10px] ${line} ${muted}`}>
          {[
            tagEnabled(s, "showStructure") ? "Structure: Standard" : null,
            tagEnabled(s, "showAnnualCtc") ? "Annual CTC: Rs. 14,52,000.00" : null,
            tagEnabled(s, "showMonthlyCtc") ? "Monthly CTC: Rs. 1,21,000.00" : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}

      {(tagEnabled(s, "showAttendance") || tagEnabled(s, "showLeave")) && (
        <div className={`flex flex-wrap gap-3 border-b px-4 py-1.5 text-[10px] ${line} ${muted}`}>
          {tagEnabled(s, "showAttendance") && <span>Paid days: 30 · LOP: 0</span>}
          {tagEnabled(s, "showLeave") && <span>Leave: CL 1 · SL 0</span>}
        </div>
      )}

      {tagEnabled(s, "showComponents") && (
        <div className={`grid grid-cols-2 gap-0 border-b text-[10px] ${line}`}>
          <div className={`border-r px-3 py-2 ${line}`}>
            <p className="mb-1 font-semibold uppercase tracking-wide">Earnings</p>
            {tagEnabled(s, "groupEarningsByCategory") && (
              <p className={`mb-1 text-[9px] font-semibold ${muted}`}>Fixed</p>
            )}
            {earn.map(([k, v]) => (
              <div key={k} className="flex justify-between py-0.5">
                <span>{k}</span>
                <span className="tabular-nums">{v}</span>
              </div>
            ))}
            <div className="mt-1 flex justify-between border-t pt-1 font-semibold">
              <span>Gross</span>
              <span className="tabular-nums">1,21,050.00</span>
            </div>
          </div>
          <div className="px-3 py-2">
            <p className="mb-1 font-semibold uppercase tracking-wide">Deductions</p>
            {ded.map(([k, v]) => (
              <div key={k} className="flex justify-between py-0.5">
                <span>{k}</span>
                <span className="tabular-nums">{v}</span>
              </div>
            ))}
            <div className="mt-1 flex justify-between border-t pt-1 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">13,294.00</span>
            </div>
          </div>
        </div>
      )}

      {tagEnabled(s, "showStatutory") && (
        <div className={`border-b px-4 py-1.5 text-[10px] ${line} ${muted}`}>
          Statutory · PF: Rs. 1,800.00 | PT: Rs. 200.00
        </div>
      )}

      {tagEnabled(s, "showTaxDetails") && (
        <div className={`border-b px-4 py-1.5 text-[10px] ${line}`}>
          <span className={label}>Tax / TDS: </span>
          <span className="font-medium tabular-nums">Rs. 11,294.00</span>
        </div>
      )}

      {tagEnabled(s, "showTaxComputation") && (
        <div className={`border-b px-4 py-2 text-[10px] ${line}`}>
          <p className="mb-1 font-semibold">Detailed tax computation</p>
          <div className={`grid grid-cols-2 gap-x-2 gap-y-0.5 ${muted}`}>
            <span>Regime: NEW</span>
            <span className="text-right">FY 2026-27</span>
            <span>Taxable income</span>
            <span className="text-right tabular-nums">Rs. 13,30,000.00</span>
            <span>Base tax</span>
            <span className="text-right tabular-nums">Rs. 1,30,000.00</span>
            <span>Cess</span>
            <span className="text-right tabular-nums">Rs. 5,200.00</span>
            <span>Annual liability</span>
            <span className="text-right tabular-nums">Rs. 1,35,200.00</span>
            <span>TDS this month</span>
            <span className="text-right tabular-nums font-medium text-[hsl(var(--ds-fg))]">Rs. 11,294.00</span>
          </div>
        </div>
      )}

      {tagEnabled(s, "showYtd") && (
        <div className={`border-b px-4 py-1.5 text-[10px] ${line} ${muted}`}>
          YTD Gross: Rs. 1,21,050.00 · YTD TDS: Rs. 11,294.00
        </div>
      )}

      <div className={`flex items-center justify-between px-4 py-3 ${darkMode ? "bg-emerald-950/30" : "bg-emerald-50"}`}>
        <span className="text-xs font-semibold">Net pay</span>
        <span className="text-sm font-bold tabular-nums text-emerald-600">Rs. 1,07,756.00</span>
      </div>
      {tagEnabled(s, "showNetInWords") && (
        <p className={`px-4 pb-2 text-[9px] italic ${muted}`}>
          Rupees One Lakh Seven Thousand Seven Hundred Fifty Six Only
        </p>
      )}
      {s.footerText ? (
        <p className={`border-t px-4 py-2 text-[9px] italic ${line} ${muted}`}>{s.footerText}</p>
      ) : null}
    </div>
  );
}

function moveSection(order, id, dir) {
  const list = normalizeSectionOrder(order);
  const idx = list.indexOf(id);
  if (idx < 0) return list;
  const swap = idx + dir;
  if (swap < 0 || swap >= list.length) return list;
  const next = [...list];
  [next[idx], next[swap]] = [next[swap], next[idx]];
  return next;
}

function DesignerControls({ draftStyle, setDraftStyle, darkMode, border, muted }) {
  const order = normalizeSectionOrder(draftStyle?.sectionOrder);
  const selectedTags = Array.isArray(draftStyle?.selectedTags) ? draftStyle.selectedTags : [];

  const toggleMergeTag = (tag) => {
    const mapsTo = tag.mapsTo;
    const on = !selectedTags.includes(tag.key);
    setDraftStyle((prev) => {
      const nextSelected = on
        ? [...(prev.selectedTags || []).filter((k) => k !== tag.key), tag.key]
        : (prev.selectedTags || []).filter((k) => k !== tag.key);
      const next = { ...prev, selectedTags: nextSelected };
      if (mapsTo) next[mapsTo] = on;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>
          Section order
        </p>
        <p className={`mb-2 text-[10px] ${muted}`}>
          Reorder how blocks appear on the PDF. Disable a block via field tags below.
        </p>
        <div className="space-y-1">
          {order.map((id, idx) => {
            const meta = SECTION_BLOCKS.find((s) => s.id === id);
            return (
              <div
                key={id}
                className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs ${border}`}
              >
                <span className={`w-5 tabular-nums ${muted}`}>{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{meta?.label || id}</p>
                  {meta?.hint ? <p className={`text-[10px] ${muted}`}>{meta.hint}</p> : null}
                </div>
                <button
                  type="button"
                  className="rounded border p-1 disabled:opacity-30"
                  disabled={idx === 0}
                  onClick={() =>
                    setDraftStyle((prev) => ({
                      ...prev,
                      sectionOrder: moveSection(prev.sectionOrder, id, -1),
                    }))
                  }
                  aria-label="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  className="rounded border p-1 disabled:opacity-30"
                  disabled={idx === order.length - 1}
                  onClick={() =>
                    setDraftStyle((prev) => ({
                      ...prev,
                      sectionOrder: moveSection(prev.sectionOrder, id, 1),
                    }))
                  }
                  aria-label="Move down"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>
          System tags (click to include)
        </p>
        <div className="space-y-3">
          {MERGE_TAGS.map((group) => (
            <div key={group.group}>
              <p className={`mb-1 text-[10px] font-medium ${muted}`}>{group.group}</p>
              <div className="flex flex-wrap gap-1.5">
                {group.tags.map((t) => {
                  const active =
                    selectedTags.includes(t.key) ||
                    (t.mapsTo ? draftStyle?.[t.mapsTo] !== false : false);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      title={t.label}
                      onClick={() => toggleMergeTag(t)}
                      className={`rounded-full border px-2 py-1 font-mono text-[10px] ${
                        active
                          ? "border-[hsl(var(--ds-primary))]/50 bg-[hsl(var(--ds-primary))]/10 text-[hsl(var(--ds-primary))]"
                          : `${border} ${muted}`
                      }`}
                    >
                      {t.key}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>
          Field / block options
        </p>
        <div className="space-y-3">
          {PAYSLIP_STYLE_TAGS.map((group) => (
            <div key={group.group}>
              <p className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>
                {group.group}
              </p>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {group.tags.map((t) => (
                  <label
                    key={t.key}
                    className={`flex cursor-pointer items-start gap-2 rounded-lg border px-2.5 py-2 text-xs ${border} ${
                      darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"
                    }`}
                    title={t.hint}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={draftStyle?.[t.key] !== false}
                      onChange={(e) =>
                        setDraftStyle((prev) => ({ ...prev, [t.key]: e.target.checked }))
                      }
                    />
                    <span>
                      <span className="font-medium">{t.label}</span>
                      {t.hint ? (
                        <span className={`mt-0.5 block text-[10px] ${muted}`}>{t.hint}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TemplatePickerModal({
  darkMode,
  open,
  templates,
  templateId,
  payMonth,
  busy,
  companyLogo,
  companyName,
  canEditTemplates = true,
  onClose,
  onSelect,
  onCustomise,
  onCreate,
}) {
  const [hoverId, setHoverId] = useState(templateId);
  useEffect(() => {
    if (open) setHoverId(templateId);
  }, [open, templateId]);

  const previewTpl = useMemo(
    () => templates.find((t) => Number(t.id) === Number(hoverId)) || templates[0] || null,
    [templates, hoverId]
  );

  if (!open || typeof document === "undefined") return null;

  const border = darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[min(92vh,820px)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border shadow-2xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Payslip template designer"
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${border}`}>
          <div>
            <h3 className="text-sm font-semibold">Payslip template designer</h3>
            <p className={`text-[11px] ${muted}`}>
              Choose one of 5 standard templates, or customise tags (logo, address, masters, leave, tax…)
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1.5 hover:bg-black/5" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[260px_1fr]">
          <div className={`min-h-0 overflow-y-auto border-b p-2 md:border-b-0 md:border-r ${border}`}>
            {templates.map((t) => {
              const meta = LAYOUT_META[t.templateKey] || { title: t.name, blurb: t.isSystem ? "System" : "Custom" };
              const active = Number(t.id) === Number(hoverId);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setHoverId(t.id)}
                  className={`mb-1 w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
                    active
                      ? darkMode
                        ? "bg-[hsl(var(--ds-primary))]/20 ring-1 ring-[hsl(var(--ds-primary))]/40"
                        : "bg-red-50 ring-1 ring-[hsl(var(--ds-primary))]/30"
                      : darkMode
                      ? "hover:bg-[hsl(var(--ds-muted))]"
                      : "hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold">{meta.title || t.name}</span>
                    {t.isSystem ? (
                      <span className={`text-[9px] uppercase ${muted}`}>Std</span>
                    ) : (
                      <span className="text-[9px] uppercase text-[hsl(var(--ds-primary))]">Custom</span>
                    )}
                  </div>
                  <p className={`mt-0.5 line-clamp-2 text-[10px] ${muted}`}>{meta.blurb}</p>
                </button>
              );
            })}
            {canEditTemplates ? (
              <button
                type="button"
                onClick={onCreate}
                className={`mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-dashed px-3 py-2 text-[11px] font-medium ${border} ${muted}`}
              >
                <Plus size={12} /> New custom template
              </button>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <PayslipMockPreview
                template={previewTpl}
                darkMode={darkMode}
                payMonth={payMonth}
                companyLogo={companyLogo}
                companyName={companyName}
              />
              {previewTpl && (
                <div className={`mt-3 flex flex-wrap gap-1.5 text-[10px] ${muted}`}>
                  {PAYSLIP_STYLE_TAGS.flatMap((g) => g.tags).map((x) => (
                    <span
                      key={x.key}
                      className={`rounded-full border px-2 py-0.5 ${border} ${
                        previewTpl.styleJson?.[x.key] === false ? "opacity-40 line-through" : ""
                      }`}
                    >
                      {x.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className={`flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-4 py-3 ${border}`}>
              {canEditTemplates ? (
                <button
                  type="button"
                  disabled={!previewTpl || busy}
                  onClick={() => onCustomise?.(previewTpl)}
                  className="rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-45"
                >
                  Customise tags
                </button>
              ) : null}
              <button
                type="button"
                disabled={!previewTpl || busy}
                onClick={() => onSelect(previewTpl?.id)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
              >
                <CheckCircle2 size={13} /> Use this template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmModal({ darkMode, open, title, children, confirmLabel, busy, onClose, onConfirm }) {
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100065] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-w-md overflow-hidden rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={`flex items-center justify-between border-b px-4 py-3 ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"}`}>
          <h3 className="text-sm font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="rounded p-1">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">{children}</div>
        <div className={`flex justify-end gap-2 border-t px-4 py-3 ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"}`}>
          <button type="button" onClick={onClose} className="rounded-lg border px-3 py-1.5 text-xs">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function PayrollPayslipsPanel({
  darkMode,
  run,
  payMonth,
  finalized,
  onUpdated,
  masterLists = {},
  processedEmployees = [],
  reloadToken = 0,
}) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canPublish =
    isAdmin ||
    hasScreenAccess("payroll", "payslips", "approve") ||
    hasScreenAccess("payroll", "pay_proc_payslips", "approve");
  const canExport =
    isAdmin ||
    hasScreenAccess("payroll", "payslips", "export") ||
    hasScreenAccess("payroll", "pay_proc_payslips", "export");
  const canEditTemplates =
    isAdmin ||
    hasScreenAccess("payroll", "payslips", "edit") ||
    hasScreenAccess("payroll", "pay_proc_payslips", "edit");
  const { confirm, dialogNode } = useConfirmDialog();

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(run?.payslipTemplateId || null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [payslipRows, setPayslipRows] = useState([]);
  const [payslipSummary, setPayslipSummary] = useState({
    publishedCount: 0,
    unpublishedCount: 0,
    totalCount: 0,
    allPublished: false,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [groupPolicy, setGroupPolicy] = useState({ allEmployees: true });
  const [showGroupFilter, setShowGroupFilter] = useState(false);
  const [actionChooser, setActionChooser] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [draftStyle, setDraftStyle] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [cloneFromId, setCloneFromId] = useState("");
  const [emailConfirmModal, setEmailConfirmModal] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [payslipMainTab, setPayslipMainTab] = useState("payslips"); // payslips | reimbursement
  const [companyName, setCompanyName] = useState("");
  const [pdfPreview, setPdfPreview] = useState({
    isOpen: false,
    url: null,
    fileName: null,
  });

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const card = darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-surface))]";
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] ${
    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
  }`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = getAuthJson("companyData", null);
      if (data?.logo) {
        if (!cancelled) {
          setCompanyLogo(data.logo);
          setCompanyName(data.name || "");
        }
      }
      try {
        const user = getAuthJson("currentUser", null);
        const companyId = user?.companyId || data?.id;
        if (!companyId) return;
        const { companyService } = await import("../../../services/api");
        const res = await companyService.getCompanyData(companyId);
        const company = res?.company || res?.data || res;
        if (!cancelled && company) {
          if (company.logo) setCompanyLogo(company.logo);
          if (company.name) setCompanyName(company.name);
        }
      } catch {
        /* keep authStorage logo */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editorOpen, pickerOpen]);

  const loadPayslipStatus = useCallback(async () => {
    if (!run?.id || !finalized) {
      setPayslipRows([]);
      setPayslipSummary({
        publishedCount: 0,
        unpublishedCount: 0,
        totalCount: 0,
        allPublished: false,
      });
      return;
    }
    setListLoading(true);
    try {
      const res = await payrollOpsService.listRunPayslips(run.id);
      const data = res?.data || res;
      setPayslipRows(Array.isArray(data?.rows) ? data.rows : []);
      setPayslipSummary({
        publishedCount: Number(data?.publishedCount) || 0,
        unpublishedCount: Number(data?.unpublishedCount) || 0,
        totalCount: Number(data?.totalCount) || 0,
        allPublished: !!data?.allPublished,
      });
    } catch (err) {
      setPayslipRows([]);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load payslip status");
    } finally {
      setListLoading(false);
    }
  }, [run?.id, finalized]);

  useEffect(() => {
    loadPayslipStatus();
  }, [loadPayslipStatus, run?.payslipsPublishedAt, reloadToken]);

  const employeePool = useMemo(() => {
    const byId = new Map((processedEmployees || []).map((e) => [Number(e.id), e]));
    return (payslipRows || []).map((row) => {
      const emp = byId.get(Number(row.employeeId)) || {};
      return {
        ...emp,
        id: Number(row.employeeId),
        empCode: row.empCode || emp.empCode || String(row.employeeId),
        name: row.name || emp.name || "",
        netPay: row.netPay,
        published: !!row.published,
        publishedAt: row.publishedAt,
        documentId: row.documentId,
        fileName: row.fileName,
        branchId: row.branchId ?? emp.branchId,
        departmentId: row.departmentId ?? emp.departmentId,
        designationId: row.designationId ?? emp.designationId,
        gradeId: row.gradeId ?? emp.gradeId,
        categoryId: row.categoryId ?? emp.categoryId,
        subDepartmentId: row.subDepartmentId ?? emp.subDepartmentId,
        location: row.location ?? emp.location,
        employmentType: row.employmentType ?? emp.employmentType,
      };
    });
  }, [payslipRows, processedEmployees]);

  const unpublishedEmployees = useMemo(
    () => employeePool.filter((e) => !e.published),
    [employeePool]
  );
  const publishedEmployees = useMemo(
    () => employeePool.filter((e) => e.published),
    [employeePool]
  );

  const filteredRows = useMemo(() => {
    let list = [...employeePool];
    if (statusFilter === "published") list = list.filter((e) => e.published);
    if (statusFilter === "unpublished") list = list.filter((e) => !e.published);
    list = filterEmployeesByApplicability(list, groupPolicy);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (e) =>
          String(e.empCode || "").toLowerCase().includes(q) ||
          String(e.name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [employeePool, statusFilter, groupPolicy, search]);

  const openPublishChooser = () => {
    if (!templateId) {
      toast.error("Choose a payslip template first");
      setPickerOpen(true);
      return;
    }
    if (!unpublishedEmployees.length) {
      toast.error("All processed employees are already published");
      return;
    }
    setActionChooser({ action: "publish" });
  };

  const openUnpublishChooser = () => {
    if (!publishedEmployees.length) {
      toast.error("No published payslips to unpublish");
      return;
    }
    setActionChooser({ action: "unpublish" });
  };

  const handleChooserConfirm = async ({ employeeIds }) => {
    if (!run?.id || !employeeIds?.length) return;
    const ids = employeeIds.map(Number).filter((id) => id > 0);
    if (!ids.length) return;

    if (actionChooser?.action === "publish") {
      setActionChooser(null);
      setBusy(true);
      setBatchProgress({ action: "publish", done: 0, total: ids.length });
      const chunks = chunkIds(ids, PUBLISH_CHUNK_SIZE);
      let done = 0;
      let publishedTotal = 0;
      let errorTotal = 0;
      let lastData = null;
      try {
        for (const chunk of chunks) {
          const res = await payrollOpsService.publishPayslips(run.id, {
            templateId,
            employeeIds: chunk,
          });
          lastData = res?.data || lastData;
          publishedTotal += Number(res?.publish?.publishedCount ?? chunk.length) || 0;
          errorTotal += Array.isArray(res?.publish?.errors) ? res.publish.errors.length : 0;
          done += chunk.length;
          setBatchProgress({ action: "publish", done: Math.min(done, ids.length), total: ids.length });
        }
        onUpdated?.(lastData);
        await loadPayslipStatus();
        if (errorTotal > 0) {
          toast.error(
            `Published with ${errorTotal} error(s). ${publishedTotal} ok — download builds PDF on demand.`
          );
        } else {
          toast.success(
            `Published ${publishedTotal || ids.length} payslip(s) to ESS (PDF generates when downloaded)`
          );
        }
        setTimeout(() => setBatchProgress(null), 900);
      } catch (err) {
        setBatchProgress(null);
        toast.error(err?.response?.data?.message || err?.message || "Publish failed");
        await loadPayslipStatus();
      } finally {
        setBusy(false);
      }
      return;
    }

    if (actionChooser?.action === "unpublish") {
      const ok = await confirm({
        title: "Unpublish payslips",
        message: `Remove payslips from ESS for ${ids.length} employee(s)? Employees will no longer see them in Payroll → Payslips.`,
        confirmText: "Unpublish",
        cancelText: "Cancel",
        danger: true,
      });
      if (!ok) return;
      setActionChooser(null);
      setBusy(true);
      setBatchProgress({ action: "unpublish", done: 0, total: ids.length });
      const chunks = chunkIds(ids, PUBLISH_CHUNK_SIZE);
      let done = 0;
      let unpublishedTotal = 0;
      let lastData = null;
      try {
        for (const chunk of chunks) {
          const res = await payrollOpsService.unpublishPayslips(run.id, { employeeIds: chunk });
          lastData = res?.data || lastData;
          unpublishedTotal += Number(res?.unpublish?.removedCount ?? chunk.length) || 0;
          done += chunk.length;
          setBatchProgress({
            action: "unpublish",
            done: Math.min(done, ids.length),
            total: ids.length,
          });
        }
        onUpdated?.(lastData);
        await loadPayslipStatus();
        toast.success(`Unpublished ${unpublishedTotal || ids.length} payslip(s) from ESS`);
        setTimeout(() => setBatchProgress(null), 900);
      } catch (err) {
        setBatchProgress(null);
        toast.error(err?.response?.data?.message || err?.message || "Unpublish failed");
        await loadPayslipStatus();
      } finally {
        setBusy(false);
      }
    }
  };

  const previewEmployeePayslip = async (row) => {
    if (!row?.published || !row?.id) return;
    setBusy(true);
    const toastId = toast.loading("Generating payslip PDF… please wait");
    try {
      const blob = await employeeService.downloadPayslip(
        row.id,
        monthYearLabel(payMonth),
        "standard"
      );
      const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      if (pdfPreview.url) window.URL.revokeObjectURL(pdfPreview.url);
      setPdfPreview({
        isOpen: true,
        url,
        fileName: row.fileName || `${row.empCode}-${monthYearLabel(payMonth)}.pdf`,
      });
      toast.success("Payslip ready", { id: toastId });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not load payslip PDF", {
        id: toastId,
      });
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollOpsService.listPayslipTemplates();
      const list = Array.isArray(res?.data) ? res.data : [];
      setTemplates(list);
      setTemplateId((prev) => {
        if (run?.payslipTemplateId && list.some((t) => t.id === run.payslipTemplateId)) {
          return run.payslipTemplateId;
        }
        if (prev && list.some((t) => t.id === prev)) return prev;
        return (list.find((t) => t.templateKey === "classic") || list[0])?.id || null;
      });
    } catch (err) {
      setTemplates([]);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [run?.payslipTemplateId]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => templates.find((t) => Number(t.id) === Number(templateId)) || null,
    [templates, templateId]
  );

  const selectedLabel =
    LAYOUT_META[selected?.templateKey]?.title || selected?.name || "None selected";

  const openCustomise = (tpl) => {
    if (!tpl) return;
    setEditTarget(tpl);
    setDraftName(tpl.name || "");
    const style = { ...(tpl.styleJson || {}) };
    style.sectionOrder = normalizeSectionOrder(style.sectionOrder);
    if (!Array.isArray(style.selectedTags)) style.selectedTags = [];
    setDraftStyle(style);
    setEditorOpen(true);
  };

  const closePdfPreview = () => {
    if (pdfPreview.url) window.URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview({ isOpen: false, url: null, fileName: null });
  };

  const previewPdf = async () => {
    if (!draftStyle) return;
    setBusy(true);
    const toastId = toast.loading("Generating preview PDF… please wait");
    try {
      const blob = await payrollOpsService.previewPayslipTemplatePdf({
        styleJson: draftStyle,
        runId: run?.id || null,
        logoOverride: companyLogo || null,
      });
      const pdfBlob =
        blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      if (pdfPreview.url) window.URL.revokeObjectURL(pdfPreview.url);
      setPdfPreview({
        isOpen: true,
        url,
        fileName: `${draftName || "payslip-template"}-preview.pdf`,
      });
      toast.success("Preview ready", { id: toastId });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "PDF preview failed", {
        id: toastId,
      });
    } finally {
      setBusy(false);
    }
  };

  const saveStyle = async () => {
    if (!editTarget || !draftStyle) return;
    setBusy(true);
    try {
      const styleJson = {
        ...draftStyle,
        sectionOrder: normalizeSectionOrder(draftStyle.sectionOrder),
      };
      const res = await payrollOpsService.updatePayslipTemplate(editTarget.id, {
        name: draftName.trim() || editTarget.name,
        styleJson,
      });
      toast.success(res?.message || "Template saved");
      setEditorOpen(false);
      await load();
      if (res?.data?.id) setTemplateId(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const createStyle = async () => {
    if (!newName.trim()) {
      toast.error("Enter a style name");
      return;
    }
    setBusy(true);
    try {
      const base = templates.find((t) => Number(t.id) === Number(cloneFromId));
      const res = await payrollOpsService.createPayslipTemplate({
        name: newName.trim(),
        styleJson: base?.styleJson || undefined,
      });
      toast.success("Custom style created");
      setCreateOpen(false);
      setNewName("");
      await load();
      if (res?.data?.id) {
        setTemplateId(res.data.id);
        setPickerOpen(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!run?.id) return;
    if (!templateId) {
      toast.error("Choose a payslip template first");
      setPickerOpen(true);
      return;
    }
    setBusy(true);
    try {
      const res = await payrollOpsService.publishPayslips(run.id, { templateId });
      toast.success(
        res?.message || "Published to ESS — PDF generates when downloaded"
      );
      setEmailConfirmModal(null);
      onUpdated?.(res?.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadBulk = async () => {
    if (!payMonth) return;
    setBusy(true);
    const toastId = toast.loading("Preparing payslip ZIP… please wait");
    try {
      // Prefer this run's employees so ZIP matches the payslip grid (not every company).
      const employeeIds = [
        ...new Set(
          (payslipRows || [])
            .map((r) => Number(r.employeeId))
            .filter((id) => Number.isFinite(id) && id > 0)
        ),
      ];
      const response = await employeeService.downloadBulkPayslips({
        monthYear: monthYearLabel(payMonth),
        employeeIds,
      });
      const blob = response?.data;
      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payslips-${payMonth}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Download started", { id: toastId });
      } else {
        toast.success("Download requested", { id: toastId });
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Bulk download failed — try Reports → Payslips",
        { id: toastId }
      );
    } finally {
      setBusy(false);
    }
  };

  if (!finalized) {
    return (
      <div
        className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${border} ${
          darkMode ? "bg-amber-950/30 text-amber-100" : "bg-amber-50 text-amber-950"
        }`}
      >
        <AlertTriangle className="mt-0.5 shrink-0" size={16} />
        <div>
          <p className="text-sm font-semibold">Finalise payroll first</p>
          <p className={`mt-0.5 text-xs ${darkMode ? "text-amber-200/80" : "text-amber-800"}`}>
            Publish, download and email unlock after the month is finalised.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex h-24 items-center justify-center gap-2 text-sm ${muted}`}>
        <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={16} />
        Loading…
      </div>
    );
  }

  const allPublished = payslipSummary.allPublished;
  const hasAnyPublished = payslipSummary.publishedCount > 0;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      {dialogNode}
      <div className={`flex shrink-0 gap-1 rounded-lg border p-1 ${border} ${card}`}>
        {[
          ["payslips", "Payslips"],
          ["reimbursement", "Reimbursement"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setPayslipMainTab(key)}
            className={`flex-1 rounded-md px-3 py-2 text-xs font-semibold ${
              payslipMainTab === key
                ? "bg-[hsl(var(--ds-primary))] text-white"
                : muted
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {payslipMainTab === "reimbursement" ? (
        <div className="ds-table-scroll-fill">
          <PayrollReimbursementPayslipsPanel
            darkMode={darkMode}
            payMonth={payMonth}
            finalized={finalized}
            processedEmployees={processedEmployees}
          />
        </div>
      ) : (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      {batchProgress &&
        (batchProgress.action === "publish" || batchProgress.action === "unpublish") && (
          <div className={`flex shrink-0 flex-wrap justify-center gap-8 rounded-lg border p-4 ${border}`}>
            <ProgressRing
              darkMode={darkMode}
              done={batchProgress.done}
              total={batchProgress.total}
              label={batchProgress.action === "publish" ? "Publishing" : "Unpublishing"}
              color={batchProgress.action === "publish" ? "#059669" : "#dc2626"}
            />
          </div>
        )}
      <div className={`shrink-0 rounded-xl border ${border} ${card}`}>
        <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 ${border}`}>
          <div className="flex min-w-0 items-start gap-2">
            {allPublished ? (
              <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={16} />
            ) : hasAnyPublished ? (
              <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={16} />
            ) : (
              <FileText className={`mt-0.5 shrink-0 ${muted}`} size={16} />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {allPublished
                  ? "All payslips published to ESS"
                  : hasAnyPublished
                    ? "Partially published to ESS"
                    : "Ready to publish"}
              </p>
              <p className={`truncate text-[11px] ${muted}`}>
                {payslipSummary.publishedCount}/{payslipSummary.totalCount} published
                {run?.payslipsPublishedAt
                  ? ` · Last batch ${new Date(run.payslipsPublishedAt).toLocaleString("en-IN")}`
                  : ""}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canPublish && (
              <>
                <button
                  type="button"
                  disabled={busy || !unpublishedEmployees.length}
                  onClick={openPublishChooser}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
                >
                  <Send size={12} />
                  Publish ({unpublishedEmployees.length})
                </button>
                <button
                  type="button"
                  disabled={busy || !publishedEmployees.length}
                  onClick={openUnpublishChooser}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--ds-danger))]/40 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--ds-danger))] disabled:opacity-45"
                >
                  <EyeOff size={12} />
                  Unpublish ({publishedEmployees.length})
                </button>
              </>
            )}
            {canExport && (
              <>
                <button
                  type="button"
                  disabled={busy || !hasAnyPublished}
                  onClick={downloadBulk}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-45"
                >
                  <Download size={12} /> ZIP
                </button>
                <button
                  type="button"
                  disabled={busy || !hasAnyPublished}
                  onClick={() => setEmailConfirmModal("email")}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-45"
                >
                  <Mail size={12} /> Email
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${muted}`}>Template</p>
            <p className="truncate text-sm font-medium">{selectedLabel}</p>
            {selected?.name && selected.name !== selectedLabel && (
              <p className={`truncate text-[11px] ${muted}`}>{selected.name}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--ds-primary))]/40 bg-[hsl(var(--ds-primary))]/5 px-3 py-2 text-xs font-semibold text-[hsl(var(--ds-primary))]"
          >
            <Eye size={14} /> Template designer
          </button>
        </div>
      </div>

      <div className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border ${border} ${card}`}>
        <div className={`ds-page-toolbar flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2 ${border}`}>
          <div className="relative min-w-[180px] flex-1">
            <Search className={`pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${muted}`} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code or name…"
              className={`${inputCls} pl-8 py-1.5 text-xs`}
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {[
              ["all", "All"],
              ["published", "Published"],
              ["unpublished", "Unpublished"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  statusFilter === key
                    ? "bg-[hsl(var(--ds-primary))] text-white"
                    : `border ${border}`
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowGroupFilter((v) => !v)}
            className={`rounded border px-2.5 py-1 text-[11px] font-medium ${border}`}
          >
            Group filter {showGroupFilter ? "▲" : "▼"}
          </button>
          <span className={`ml-auto text-[11px] ${muted}`}>
            Showing <strong className="tabular-nums">{filteredRows.length}</strong> of{" "}
            <strong className="tabular-nums">{employeePool.length}</strong>
          </span>
        </div>

        {showGroupFilter && (
          <div className={`shrink-0 border-b px-3 py-3 ${border}`}>
            <PolicyApplicabilityStepSection
              darkMode={darkMode}
              policy={groupPolicy}
              setPolicy={setGroupPolicy}
              branchesList={masterLists.branchesList || []}
              departmentsList={masterLists.departmentsList || []}
              designationsList={masterLists.designationsList || []}
              gradesList={masterLists.gradesList || []}
              categoriesList={masterLists.categoriesList || []}
              subDepartmentsList={masterLists.subDepartmentsList || []}
              title="Filter listing"
              allEmployeesLabel="All processed employees"
            />
          </div>
        )}

        {listLoading ? (
          <div className={`flex h-32 shrink-0 items-center justify-center gap-2 text-sm ${muted}`}>
            <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={16} />
            Loading employees…
          </div>
        ) : !filteredRows.length ? (
          <p className={`shrink-0 px-4 py-8 text-center text-sm ${muted}`}>
            {employeePool.length
              ? "No employees match the current filters."
              : "No processed employees for this month. Process payroll first."}
          </p>
        ) : (
          <div className="ds-table-scroll-fill">
            <table className="w-full min-w-[720px] text-xs">
              <thead className={`sticky top-0 z-10 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
                <tr>
                  <th className="px-3 py-2 text-left font-bold">Sl.</th>
                  <th className="px-3 py-2 text-left font-bold">Code</th>
                  <th className="px-3 py-2 text-left font-bold">Name</th>
                  <th className="px-3 py-2 text-right font-bold">Net pay</th>
                  <th className="px-3 py-2 text-left font-bold">Status</th>
                  <th className="px-3 py-2 text-center font-bold">Preview</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <tr
                    key={row.id}
                    className={darkMode ? "border-t border-[hsl(var(--ds-border))]" : "border-t border-[hsl(var(--ds-border))]"}
                  >
                    <td className="px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{row.empCode}</td>
                    <td className="px-3 py-2">{row.name || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInr(row.netPay)}</td>
                    <td className="px-3 py-2">
                      {row.published ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          <CheckCircle2 size={10} /> Published
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-[hsl(var(--ds-muted))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-muted-fg))]">
                          Unpublished
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        disabled={busy || !row.published}
                        onClick={() => previewEmployeePayslip(row)}
                        className="inline-flex text-[hsl(var(--ds-primary))] disabled:cursor-not-allowed disabled:opacity-35"
                        title={row.published ? "Preview published payslip PDF" : "Publish first to preview"}
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
      )}

      {actionChooser && (
        <PayrollActionChooserModal
          darkMode={darkMode}
          open={!!actionChooser}
          title={actionChooser.action === "publish" ? "Publish payslips" : "Unpublish payslips"}
          subtitle={
            actionChooser.action === "publish"
              ? "Unlock download for these employees (All / Selected / Group-wise). PDF is built on download with the selected template and company logo when available."
              : "Choose published employees to remove from ESS (All / Selected / Group-wise)."
          }
          employees={
            actionChooser.action === "publish" ? unpublishedEmployees : publishedEmployees
          }
          masterLists={masterLists}
          confirmLabel={actionChooser.action === "publish" ? "Publish" : "Unpublish"}
          onClose={() => setActionChooser(null)}
          onConfirm={handleChooserConfirm}
        />
      )}

      <TemplatePickerModal
        darkMode={darkMode}
        open={pickerOpen}
        templates={templates}
        templateId={templateId}
        payMonth={payMonth}
        busy={busy}
        companyLogo={companyLogo}
        companyName={companyName}
        canEditTemplates={canEditTemplates}
        onClose={() => setPickerOpen(false)}
        onSelect={(id) => {
          setTemplateId(id);
          setPickerOpen(false);
          toast.success("Template selected");
        }}
        onCustomise={(tpl) => {
          if (!canEditTemplates) return;
          setPickerOpen(false);
          openCustomise(tpl);
        }}
        onCreate={() => {
          if (!canEditTemplates) return;
          setCloneFromId(selected?.id || templateId || "");
          setPickerOpen(false);
          setCreateOpen(true);
        }}
      />

      {editorOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[100070] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setEditorOpen(false)}
            role="presentation"
          >
            <div
              className={`flex max-h-[min(94vh,920px)] w-full max-w-6xl flex-col overflow-hidden rounded-xl border shadow-xl ${
                darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${border}`}>
                <div>
                  <h3 className="text-sm font-semibold">Customise payslip template</h3>
                  <p className={`text-[11px] ${muted}`}>
                    Pick system tags, reorder sections, set CTC / tax / groups — then Preview PDF before save.
                    {companyLogo
                      ? " Logo loaded from Configuration → Enterprise."
                      : " No company logo found — upload under Configuration → Enterprise."}
                  </p>
                </div>
                <button type="button" onClick={() => setEditorOpen(false)} className="rounded p-1">
                  <X size={16} />
                </button>
              </div>
              <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1.1fr_minmax(300px,440px)]">
                <div className="min-h-0 space-y-3 overflow-y-auto border-b p-4 lg:border-b-0 lg:border-r">
                  <label className="block text-xs">
                    <span className={muted}>Template name</span>
                    <input
                      className={`${inputCls} mt-1`}
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                    />
                  </label>
                  <DesignerControls
                    draftStyle={draftStyle}
                    setDraftStyle={setDraftStyle}
                    darkMode={darkMode}
                    border={border}
                    muted={muted}
                  />
                  <label className="block text-xs">
                    <span className={muted}>Footer text</span>
                    <textarea
                      rows={2}
                      className={`${inputCls} mt-1 resize-none`}
                      value={draftStyle?.footerText || ""}
                      onChange={(e) =>
                        setDraftStyle((prev) => ({ ...prev, footerText: e.target.value }))
                      }
                    />
                  </label>
                </div>
                <div className="min-h-0 overflow-y-auto bg-[hsl(var(--ds-muted))]/60 p-4 dark:bg-transparent">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${muted}`}>
                      Layout preview
                    </p>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={previewPdf}
                      className="inline-flex items-center gap-1 rounded-lg border border-[hsl(var(--ds-primary))]/40 bg-[hsl(var(--ds-primary))]/5 px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--ds-primary))] disabled:opacity-45"
                    >
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                      Preview PDF
                    </button>
                  </div>
                  <PayslipMockPreview
                    template={{ ...editTarget, styleJson: draftStyle }}
                    darkMode={darkMode}
                    payMonth={payMonth}
                    companyLogo={companyLogo}
                    companyName={companyName}
                  />
                </div>
              </div>
              <div className={`flex shrink-0 flex-wrap items-center justify-between gap-2 border-t px-4 py-3 ${border}`}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={previewPdf}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                  Preview PDF before save
                </button>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditorOpen(false)} className="rounded-lg border px-3 py-1.5 text-xs">
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={saveStyle}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Save template
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {pdfPreview.isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-[100080] flex items-center justify-center bg-black/60 p-4">
            <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-[hsl(var(--ds-surface))] shadow-2xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{pdfPreview.fileName}</h3>
                <div className="flex items-center gap-2">
                  {pdfPreview.url && (
                    <a
                      href={pdfPreview.url}
                      download={pdfPreview.fileName || "payslip-preview.pdf"}
                      className="rounded-lg border px-3 py-1.5 text-xs font-medium text-[hsl(var(--ds-fg))]"
                    >
                      Download
                    </a>
                  )}
                  <button type="button" onClick={closePdfPreview} className="rounded p-1 text-[hsl(var(--ds-muted-fg))]">
                    <X size={18} />
                  </button>
                </div>
              </div>
              <iframe
                title={pdfPreview.fileName || "Payslip PDF preview"}
                src={pdfPreview.url || undefined}
                className="h-[min(75vh,720px)] w-full border-0"
              />
            </div>
          </div>,
          document.body
        )}

      <ConfirmModal
        darkMode={darkMode}
        open={createOpen}
        title="Create custom style"
        confirmLabel="Create"
        busy={busy}
        onClose={() => setCreateOpen(false)}
        onConfirm={createStyle}
      >
        <label className="block text-xs">
          <span className={muted}>Name</span>
          <input
            className={`${inputCls} mt-1`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Brand compact"
          />
        </label>
        <label className="block text-xs">
          <span className={muted}>Clone from</span>
          <select
            className={`${inputCls} mt-1`}
            value={cloneFromId || ""}
            onChange={(e) => setCloneFromId(e.target.value)}
          >
            <option value="">Classic defaults</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      </ConfirmModal>

      <ConfirmModal
        darkMode={darkMode}
        open={emailConfirmModal === "email"}
        title="Email payslips"
        confirmLabel="Queue email"
        busy={busy}
        onClose={() => setEmailConfirmModal(null)}
        onConfirm={() => {
          setEmailConfirmModal(null);
          toast.success(
            "Email queue noted — use Reports → Payslips for bulk mail when SMTP is configured."
          );
        }}
      >
        <p className={muted}>
          Sends published payslips for <strong>{monthYearLabel(payMonth)}</strong> to employees with
          work email.
        </p>
      </ConfirmModal>
    </div>
  );
}
